import React, { useEffect, useState, useMemo } from "react";
import useAllPendingUpdates from "../Utils/useAllPendingUpdates";
import usePendingDeletes from "../Utils/usePendingDeletes";
import Skeleton from "react-loading-skeleton";
import { ToastContainer } from "react-toastify";
import { useFetchVessels } from "../Utils/useFetchVessels";
import { HiOutlineDocumentText } from "react-icons/hi";
import { RiShipLine } from "react-icons/ri";
import { FiInfo } from "react-icons/fi";
import { MdOutlineDescription, MdOutlinePending } from "react-icons/md";
import "react-toastify/dist/ReactToastify.css";
import "react-loading-skeleton/dist/skeleton.css";
import "./allpending.css";

const AllpendingUpdates = () => {
  const [selectedVesselId, setSelectedVesselId] = useState(null);
  const {
    data: vesselsData,
    isLoading: vesselsLoading,
    error: vesselsError,
  } = useFetchVessels();
  const {
    data: updatesData,
    isLoading: updatesLoading,
    isError: updatesError,
  } = useAllPendingUpdates(selectedVesselId);
  const {
    data: deletesData,
    isLoading: deletesLoading,
    error: deletesError,
  } = usePendingDeletes(selectedVesselId);

  const vessels = useMemo(() => vesselsData?.data || [], [vesselsData]);

  const updatePermissions = updatesData?.data?.updatePermissions || {};

  // Set the first vessel as the default
  useEffect(() => {
    if (vessels.length > 0 && !selectedVesselId) {
      setSelectedVesselId(vessels[0]._id);
    }
  }, [vessels, selectedVesselId]);

  const handleVesselSelect = (vesselId) => {
    setSelectedVesselId(vesselId);
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        <div className="dashboard-row">
          {/* Select a Vessel Column - Narrower */}
          <div className="dashboard-column vessel-selection-column">
            <div className="dashboard-card">
              <div className="card-header">
                <RiShipLine className="card-icon" />
                <h2>Select a Vessel</h2>
              </div>
              
              <div className="card-content">
        {vesselsLoading ? (
                  <Skeleton count={2} height={60} borderRadius={6} />
        ) : vesselsError ? (
                  <p className="error-message">Error loading vessels: {vesselsError.message}</p>
                ) : vessels.length === 0 ? (
                  <p className="no-data-message">No vessels available</p>
        ) : (
                  <ul className="vessel-list">
            {vessels.map((vessel) => (
              <li
                key={vessel._id}
                onClick={() => handleVesselSelect(vessel._id)}
                className={selectedVesselId === vessel._id ? "selected" : ""}
              >
                        <span>{vessel.primaryInformation?.vesselType || "Unknown Type"}</span>
                        <p>{vessel.vesselInformation?.mmsiNumber || "No MMSI Number"}</p>
              </li>
            ))}
          </ul>
        )}
              </div>
            </div>
          </div>
          
          {/* Pending Updates Column - Wider */}
          <div className="dashboard-column updates-column">
            <div className="dashboard-card">
              <div className="card-header">
                <MdOutlinePending className="card-icon" />
                <h2>Pending Updates</h2>
              </div>
              
              <div className="card-content">
                {vessels.length === 0 ? (
                  <div className="empty-state-container">
                    <FiInfo className="empty-state-icon" />
                    <p>No vessels available. Please add vessels to see pending updates.</p>
                  </div>
                ) : !selectedVesselId ? (
                  <div className="empty-state-container">
                    <FiInfo className="empty-state-icon" />
                    <p>Please select a vessel to view pending updates.</p>
                  </div>
                ) : updatesLoading ? (
                  <div className="skeleton-container">
                    <Skeleton count={3} height={20} style={{ marginBottom: 10 }} />
                  </div>
                ) : updatesError ? (
                  <p className="error-message">
                    <FiInfo className="error-icon" /> Error loading updates: {updatesError.message}
                  </p>
                ) : Object.keys(updatePermissions).filter(key => 
                  updatePermissions[key].appliedToChange
                ).length > 0 ? (
                  <div className="updates-list">
                    {Object.keys(updatePermissions).map((key) => {
                      const update = updatePermissions[key];
                      if (!update.appliedToChange) return null;
                      
                      return (
                        <div key={key} className="update-item">
                          <h4>
                            <HiOutlineDocumentText className="item-icon" />
                            {key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                          </h4>
                          {/* Update details content */}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="empty-state-container">
                    <FiInfo className="empty-state-icon" />
                    <p>No pending updates available for this vessel.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {selectedVesselId && vessels.length > 0 && (
          <div className="dashboard-row">
            {/* Vessel Details (conditional) */}
            <div className="dashboard-column vessel-details-column">
              <div className="dashboard-card">
                <div className="card-header">
                  <FiInfo className="card-icon" />
                  <h2>Vessel Details</h2>
      </div>

                <div className="card-content">
            {vessels
              .filter((vessel) => vessel._id === selectedVesselId)
              .map((vessel) => (
                      <div key={vessel._id} className="vessel-details">
                  <p>
                          <strong>Type:</strong> {vessel.primaryInformation?.vesselType || "Not specified"}
                        </p>
                        <p>
                          <strong>MMSI Number:</strong> {vessel.vesselInformation?.mmsiNumber || "Not specified"}
                  </p>
                  <p>
                          <strong>IMO Number:</strong> {vessel.vesselInformation?.imoNumber || "Not specified"}
                        </p>
                        {vessel.primaryInformation?.vesselDescription && (
                          <p>
                            <strong>Description:</strong> {vessel.primaryInformation.vesselDescription}
                  </p>
                        )}
                        
                        {vessel.images && vessel.images.length > 0 && (
                  <div className="vessel-images">
                            <h4>
                              <MdOutlineDescription className="section-icon" /> Vessel Images
                            </h4>
                            <div className="images-container">
                      {vessel.images.map((image, index) => (
                        <img key={index} src={image} alt={`Vessel ${index}`} />
                      ))}
                    </div>
                  </div>
                        )}
          </div>
                    ))}
                </div>
              </div>
            </div>
            
            {/* Pending Deletes Column */}
            <div className="dashboard-column deletes-column">
              <div className="dashboard-card">
                <div className="card-header">
                  <MdOutlinePending className="card-icon" />
                  <h2>Pending Deletes</h2>
        </div>

                <div className="card-content">
          {deletesLoading ? (
                    <div className="skeleton-container">
                      <Skeleton count={3} height={20} style={{ marginBottom: 10 }} />
                    </div>
          ) : deletesError ? (
                    <p className="error-message">
                      <FiInfo className="error-icon" /> Error loading deletes: {deletesError.message}
                    </p>
                  ) : deletesData?.data?.deletePermissions &&
                    Object.entries(deletesData.data.deletePermissions).filter(
                      ([_, deleteInfo]) => deleteInfo.appliedToDelete
                    ).length > 0 ? (
                    <div className="deletes-list">
                      {Object.entries(deletesData.data.deletePermissions).map(
              ([key, deleteInfo]) => {
                if (!deleteInfo.appliedToDelete) return null;

                return (
                  <div key={deleteInfo._id} className="delete-item">
                    <h4>
                                <HiOutlineDocumentText className="item-icon" />
                                {key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}
                    </h4>
                              {/* Delete details content */}
                            </div>
                          );
                        }
                      )}
                          </div>
                  ) : (
                    <p className="no-data-message">No pending deletes available for this vessel.</p>
                    )}
                  </div>
              </div>
            </div>
            </div>
          )}
      </div>

      <ToastContainer />
    </div>
  );
};

export default AllpendingUpdates;
